import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../services/api';
import { knowledgeBaseService } from '../services/knowledgeBaseService';
import type { Transcript } from './SearchTranscript.types';
import SearchTranscriptLog from './SearchTranscriptLog';
import { FRSSummaryView } from './FRSSummaryView';
import { ExtractionSummaryView } from './ExtractionSummaryView';
import './SearchTranscript.css';

export default function SearchTranscript() {
  const { transcriptId } = useParams<{ transcriptId: string }>();

  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedPath, setSavedPath] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [kbName, setKbName] = useState<string | null>(null);
  const [kbNameForId, setKbNameForId] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'log' | 'summary'>('log');

  const fetchTranscript = useCallback(async () => {
    if (!transcriptId) return;
    try {
      const response = await apiClient.get<Transcript>(`/transcripts/${transcriptId}`);
      setTranscript(response.data);
      setError(null);
      if (response.data.status === 'completed' || response.data.status === 'error') {
        setIsPolling(false);
      }
    } catch (err) {
      console.error('Failed to fetch transcript:', err);
      setError('Failed to load transcript. It may have expired.');
      setIsPolling(false);
    }
  }, [transcriptId]);

  useEffect(() => { fetchTranscript(); }, [fetchTranscript]);

  useEffect(() => {
    const kbId = transcript?.kb_id;
    if (!kbId) return;
    if (kbNameForId === kbId && kbName) return;
    let cancelled = false;
    (async () => {
      try {
        const kb = await knowledgeBaseService.getById(kbId);
        if (cancelled) return;
        setKbName(kb.name);
        setKbNameForId(kbId);
      } catch {
        if (cancelled) return;
        setKbName(null);
        setKbNameForId(kbId);
      }
    })();
    return () => { cancelled = true; };
  }, [transcript?.kb_id, kbName, kbNameForId]);

  useEffect(() => {
    if (transcript?.final_result?.['type'] === 'full_kb_review' || transcript?.final_result?.['type'] === 'extraction') {
      setActiveView('summary');
    }
  }, [transcript?.final_result]);

  useEffect(() => {
    if (!isPolling) return;
    const interval = setInterval(fetchTranscript, 2000);
    return () => clearInterval(interval);
  }, [isPolling, fetchTranscript]);

  const handleSave = async () => {
    if (!transcriptId) return;
    setSaving(true);
    try {
      const response = await apiClient.post<{ filepath: string }>(`/transcripts/${transcriptId}/save`);
      setSavedPath(response.data.filepath);
    } catch (err) {
      console.error('Failed to save transcript:', err);
      setError('Failed to save transcript');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
  };

  if (error && !transcript) {
    return (
      <div className="search-transcript">
        <header className="transcript-header"><h1>🔍 Search Transcript</h1></header>
        <main className="transcript-main"><div className="error-message">{error}</div></main>
      </div>
    );
  }

  if (!transcript) {
    return (
      <div className="search-transcript">
        <header className="transcript-header"><h1>🔍 Search Transcript</h1></header>
        <main className="transcript-main">
          <div className="loading-state"><div className="spinner" /><p>Loading transcript...</p></div>
        </main>
      </div>
    );
  }

  const hasTabView = transcript.final_result?.['type'] === 'full_kb_review' || transcript.final_result?.['type'] === 'extraction';

  return (
    <div className="search-transcript">
      <header className="transcript-header">
        <div className="header-left">
          <h1>🔍 Search Transcript</h1>
          <span className={`status-badge status-${transcript.status}`}>
            {transcript.status === 'running' && '⏳ '}
            {transcript.status === 'completed' && '✅ '}
            {transcript.status === 'error' && '❌ '}
            {transcript.status}
          </span>
        </div>
        <div className="header-right">
          <button className="help-toggle-button" onClick={() => setShowHelp(!showHelp)} title="Toggle search execution guide" style={{ marginRight: '10px' }}>
            {showHelp ? '📖 Hide Guide' : '📖 Show Guide'}
          </button>
          <button className="save-button" onClick={handleSave} disabled={saving || transcript.status === 'running'}>
            {saving ? 'Saving...' : '💾 Save Transcript'}
          </button>
        </div>
      </header>

      {savedPath && (
        <div className="saved-notice">✅ Transcript saved to: <code>{savedPath}</code></div>
      )}

      <div className="transcript-info">
        <div className="info-row"><span className="info-label">Search ID:</span><span className="info-value">{transcript.search_id}</span></div>
        <div className="info-row"><span className="info-label">Knowledge Base:</span><span className="info-value">{transcript.kb_id}</span></div>
        <div className="info-row"><span className="info-label">Started:</span><span className="info-value">{formatTime(transcript.started_at)}</span></div>
        {transcript.completed_at && (
          <div className="info-row"><span className="info-label">Completed:</span><span className="info-value">{formatTime(transcript.completed_at)}</span></div>
        )}
      </div>

      {hasTabView && (
        <div className="transcript-view-tabs">
          <button className={`transcript-view-tab${activeView === 'log' ? ' active' : ''}`} onClick={() => setActiveView('log')}>Technical Log</button>
          <button className={`transcript-view-tab${activeView === 'summary' ? ' active' : ''}`} onClick={() => setActiveView('summary')}>Summary</button>
        </div>
      )}

      <SearchTranscriptLog transcript={transcript} kbName={kbName} showHelp={showHelp} hidden={activeView !== 'log'} />

      {activeView === 'summary' && transcript.final_result?.['type'] === 'full_kb_review' && (
        <FRSSummaryView result={transcript.final_result} transcript={transcript} kbName={kbName} />
      )}
      {activeView === 'summary' && transcript.final_result?.['type'] === 'extraction' && (
        <ExtractionSummaryView result={transcript.final_result} transcript={transcript} kbName={kbName} />
      )}
    </div>
  );
}

